import {
  Box,
  Checkbox,
  InputAdornment,
  OutlinedInput,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { Search } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
import { SidePanelCaption, StandaloneBadge } from 'src/components';
import { ModalContainer } from 'src/components/ModalContainer';
import type {
  ModalAction,
  ModalActions,
} from 'src/components/ModalContainer/modal-container.types';
import { filterAdornmentSx } from 'src/components/ModalContainer/modal-list.styles';
import { SelectMenu } from 'src/components/SelectMenu';
import { CustomViewAttribute, CustomViewFormModel } from 'src/graphql/entities/custom-view';
import {
  attributeRowSx,
  attributeToggleSx,
} from 'src/pages/CustomViews/custom-views-form-view/draggable-list.styles';
import { capitalizeString } from 'src/utils/strings';
import { useScreenDimensions } from 'src/utils/system';
import {
  type DisplaySort,
  getNaturalSortDesc,
  getSortAttributeOptions,
  getSortOrderOptions,
  toSortDesc,
  toSortDirection,
} from './display-sort.helpers';
import { DraggableList } from './draggable-list';

interface CustomViewColumnsModalProps {
  open: boolean;
  onClose: () => void;
  attributes: CustomViewAttribute[];
  selectedColumns: CustomViewFormModel['columns'];
  onReorder: (data: CustomViewFormModel['columns']) => void;
  onSelect: (column: CustomViewAttribute) => void;
  /** Shows "Reset to default" in the footer; omitted where the columns have no saved state to go back to. */
  onReset?: () => void;
  /** Phones only: the Sort section appears when the host supplies `onSortChange`, since cards have no sortable headers. */
  sort?: DisplaySort | null;
  onSortChange?: (sort: DisplaySort) => void;
}

const NO_SORT_LABEL = 'Default';
const NO_ORDER_LABEL = '—';

const sortRowSx: SxProps<Theme> = { width: '100%', minHeight: '2.5rem', px: 0.5, gap: 0.5 };

const DisplaySectionHeading = ({ label, count }: { label: string; count?: number }) => (
  <Stack direction="row" alignItems="center" gap={0.75}>
    <SidePanelCaption>{label}</SidePanelCaption>
    {count === undefined ? null : <StandaloneBadge badgeContent={count} size="small" />}
  </Stack>
);

const DisplaySortRowContent = ({ label, value }: { label: string; value: string }) => (
  <Stack
    component="span"
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    gap={1}
    width="100%"
    minWidth={0}
    fontSize="0.8125rem"
  >
    <Typography
      component="span"
      variant="inherit"
      noWrap
      fontWeight="fontWeightRegular"
      sx={{ color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant }}
    >
      {label}
    </Typography>
    <Typography
      component="span"
      variant="inherit"
      noWrap
      fontWeight="fontWeightMedium"
      sx={{ color: (theme) => theme.palette.custom.OV.sys.onSurface }}
    >
      {value}
    </Typography>
  </Stack>
);

const DisplaySortSection = ({
  attributes,
  sort,
  onSortChange,
}: {
  attributes: CustomViewAttribute[];
  sort?: DisplaySort | null;
  onSortChange: (sort: DisplaySort) => void;
}) => {
  const attributeOptions = useMemo(() => getSortAttributeOptions(attributes), [attributes]);

  const sortAttribute = attributes.find(
    (attribute) => attribute.attribute === sort?.id && !!attribute.sortingKey,
  );
  const orderOptions = sortAttribute ? getSortOrderOptions(sortAttribute.uiType) : [];
  const orderValue = sortAttribute && sort ? toSortDirection(sort.desc) : null;

  const handleAttributeChange = (attributeId: string) => {
    const attribute = attributes.find((candidate) => candidate.attribute === attributeId);
    if (attribute) {
      onSortChange({ id: attributeId, desc: getNaturalSortDesc(attribute.uiType) });
    }
  };

  const handleOrderChange = (direction: string) => {
    if (sortAttribute) {
      onSortChange({ id: sortAttribute.attribute, desc: toSortDesc(direction) });
    }
  };

  return (
    <Stack gap={0.5} mb={2}>
      <DisplaySectionHeading label="Sort" />
      <Stack>
        <SelectMenu
          label="Sort by"
          variant="inline"
          chevron
          options={attributeOptions}
          value={sortAttribute?.attribute ?? null}
          onChange={handleAttributeChange}
          placeholder={NO_SORT_LABEL}
          search
          sectioned
          menuWidth="trigger"
          sx={sortRowSx}
          triggerContent={
            <DisplaySortRowContent
              label="Sort by"
              value={sortAttribute?.attributeLabel ?? NO_SORT_LABEL}
            />
          }
        />
        <SelectMenu
          label="Order"
          variant="inline"
          chevron
          options={orderOptions}
          value={orderValue}
          onChange={handleOrderChange}
          placeholder={NO_ORDER_LABEL}
          disabled={!sortAttribute}
          menuWidth="trigger"
          sx={sortRowSx}
          triggerContent={
            <DisplaySortRowContent
              label="Order"
              value={
                orderOptions.find((option) => option.value === orderValue)?.title ??
                NO_ORDER_LABEL
              }
            />
          }
        />
      </Stack>
    </Stack>
  );
};

// TODO refactor so this component can be used with react-hook-form
// so it is possible to use it with both raw react handlers and react-hook forms
export const CustomViewsColumnsSelector = ({
  open,
  onClose,
  attributes,
  selectedColumns,
  onSelect,
  onReorder,
  onReset,
  sort,
  onSortChange,
}: CustomViewColumnsModalProps) => {
  const { isMobile } = useScreenDimensions();
  const [filterString, setFilterString] = useState('');

  const groupedOptions = useMemo(
    () =>
      Object.entries(
        attributes
          ?.filter(
            // filters items which match the search
            (option) =>
              !filterString ||
              option.attributeLabel?.toLowerCase().includes(filterString.toLowerCase()),
          )
          .reduce<Record<string, CustomViewAttribute[]>>(
            // groups matched items by their group
            (acc, i) => ({
              ...acc,
              [i.group]: !acc?.[i.group] ? [i] : [...acc[i.group], i],
            }),
            {},
          ) ?? {},
      ),
    [filterString, attributes],
  );

  const selectedAttributes = useMemo(
    () => new Set(selectedColumns?.map((column) => column.attribute)),
    [selectedColumns],
  );

  const doneAction: ModalAction = { label: 'Done', kind: 'primary', onClick: onClose };
  const actions: ModalActions = onReset
    ? [{ label: 'Reset to default', kind: 'secondary', onClick: onReset }, doneAction]
    : [doneAction];

  return (
    <ModalContainer
      open={open}
      onClose={onClose}
      title="Display"
      size="large"
      fixedHeight
      testId="custom-views-display-modal"
      actions={actions}
    >
      {isMobile && onSortChange ? (
        <DisplaySortSection attributes={attributes} sort={sort} onSortChange={onSortChange} />
      ) : null}

      <Stack gap={1.5} mb={3}>
        <DisplaySectionHeading
          label="Visible Attributes"
          count={selectedColumns?.length ?? 0}
        />

        {selectedColumns?.length ? (
          <DraggableList
            selectedColumns={selectedColumns}
            onSelect={onSelect}
            onReorder={onReorder}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            Nothing is visible.
          </Typography>
        )}
      </Stack>

      <Stack gap={1.5}>
        <DisplaySectionHeading label="All attributes" />
        <OutlinedInput
          value={filterString}
          onChange={(event) => setFilterString(event.target.value)}
          // The container submits its primary on Enter; filtering should not close the dialog.
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
            }
          }}
          fullWidth
          placeholder="Filter results"
          inputProps={{ 'aria-label': 'Filter results' }}
          startAdornment={
            <InputAdornment position="start" sx={filterAdornmentSx}>
              <Search aria-hidden />
            </InputAdornment>
          }
        />

        {groupedOptions.length ? (
          groupedOptions.map(([group, options]) => (
            <Stack key={group} gap={0.5} pt={0.5}>
              <SidePanelCaption sx={{ px: 1 }}>{capitalizeString(group)}</SidePanelCaption>
              <Stack>
                {options.map((option) => (
                  <Box key={option.attribute} sx={attributeRowSx}>
                    <Box component="label" sx={attributeToggleSx}>
                      <Checkbox
                        size="small"
                        checked={selectedAttributes.has(option.attribute)}
                        onChange={() => onSelect(option)}
                        sx={{ p: 0 }}
                      />
                      <Typography variant="body2" noWrap minWidth={0}>
                        {option.attributeLabel}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Stack>
          ))
        ) : (
          <Typography variant="caption" color="text.secondary">
            No attribute matches
          </Typography>
        )}
      </Stack>
    </ModalContainer>
  );
};

export const CustomViewsColumnsSelectorForm = memo(CustomViewsColumnsSelector);
